import React, { useEffect, useMemo, useCallback, useState } from 'react';
import styles from './VideoSidebarMenu.module.scss';
import { PreferenceList } from './PreferenceSwiches/PreferenceList';
import { EditThumbnail } from './EditThumbnail/EditThumbnail';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import SidebarButton from './SidebarButton/SidebarButton';
import { useUserContext } from '../../../context/UserContext';
import ReviewVideo from './ReviewVideo/ReviewVideo';
import { approvalStatuses } from '../../../features/Dashboard/components/VideosFilter/constants';
import ApprovalsFeedback from './ApprovalsFeedback/ApprovalsFeedback';
import CloseButton from '../../../components/CloseButton/CloseButton';
import cx from 'classnames';
import ChaptersEditor from '../../../components/ChaptersEditor/ChaptersEditor';
import MiniTrigger from '../../../components/Sidebar/HelperSidebar/MiniTrigger';
import CallToActionEditor from '../../../components/CallToActionEditor/CallToActionEditor';
import { useCallToActionContext } from '../../../context/CallToActionContext';
import { useChaptersContext } from '../../../context/ChaptersContext';
import ResourceHelper from '../../../utils/ResourceHelper';
import Spinner from '../../../../common/components/Spinner';
import { useVoiceoverContext } from '../../../context/VoiceoverContext';
import VoiceoverEditor from '../../../components/VoiceoverEditor/VoiceoverEditor';
import { VIDEO_VERSION } from '../../../features/Dashboard/const';
import SwitchList from '../../../components/SwitchList/SwitchList';

export const VideoSidebarMenu = ({
  video,
  isVideoProcessing,
  updateVideoSettings,
  approveVideo,
  declineVideo,
  revokeVideo,
  removeFromReviewVideo,
  isUserHasEditingRights,
  draft,
  handleEditClicked,
  onChaptersEditorToggle,
  handleSaveChapters,
  onCallToActionEditorToggle,
  loadVideoVoiceovers,
  updateVoiceoversListener,
  miniRightSidebar,
  setMiniRightSidebar,
  isEditingDisabled,
  isThumbnailProcessing,
  handleChooseThumbnail,
  isEditThumbnail,
  toggleThumbnail,
  isPreferencesEditor,
  togglePreferencesEditor,
  isExternallyEdited,
  version,
  handleChangeVersion,
  editorPreference,
}) => {
  const history = useHistory();

  const [isReviewMode, setIsReviewMode] = useState(false);
  const [isApprovalsFeedbackMode, setIsApprovalsFeedbackMode] = useState(false);
  const [checkClosing, setCheckClosing] = useState(false);

  const { isChaptersEditor, openChaptersEditor } = useChaptersContext();
  const { isCallToActionOpened, openCallToAction } = useCallToActionContext();
  const { isVoiceoverOpened, openVoiceover, closeVoiceover } = useVoiceoverContext();
  const { isApprovalFlowEnabled, isReviewerUser, isCallToActionEnabled, logFirebaseEvent, isVoiceoverEnabled } = useUserContext();

  const { t } = useTranslation();


  const getEditButtonBadge = useMemo(() => {
    if (!editorPreference) {
      return null;
    }

    const editorType = editorPreference.editorType;
    if (editorType === 'advanced') {
      return 'PRO';
    }
    return 'BASIC';
  }, [editorPreference]);

  const getProcessingOrDraftTooltip = useMemo(() => {
    if (isVideoProcessing) {
      return t('my_video.tools.processing_tooltip');
    }
    if (draft) {
      return t('my_video.tools.draft_tooltip');
    }
    if (isExternallyEdited) {
      return t('my_video.tools.externally_edited_tooltip');
    }
    return '';

  }, [isVideoProcessing, draft, isExternallyEdited]);

  const isPreExistedStatus = useMemo(() => video?.approvalStatus === approvalStatuses.PRE_EXISTED.TYPE, [video]);
  const isInReviewStatus = useMemo(() => video?.approvalStatus === approvalStatuses.IN_REVIEW.TYPE, [video]);
  const isApprovedStatus = useMemo(() => video?.approvalStatus === approvalStatuses.APPROVED.TYPE, [video]);
  const isSidebarMid = useMemo(
    () =>
      isPreferencesEditor || isEditThumbnail || isReviewMode || isApprovalsFeedbackMode || isCallToActionOpened || isVoiceoverOpened,
    [isPreferencesEditor, isEditThumbnail, isReviewMode, isApprovalsFeedbackMode, isCallToActionOpened, isVoiceoverOpened]
  );
  const isSidebarMax = useMemo(() => isChaptersEditor, [isChaptersEditor]);
  const showOptionToReviewer = useMemo(
    () =>
      (isApprovalFlowEnabled && isReviewerUser && isInReviewStatus) ||
      ((!isApprovalFlowEnabled || (!isApprovedStatus && !isInReviewStatus)) && isUserHasEditingRights),
    [isApprovalFlowEnabled, isReviewerUser, isInReviewStatus, isApprovedStatus, isUserHasEditingRights]
  );
  const approvalsTitle = useMemo(() => {
    switch (video?.approvalStatus) {
      case approvalStatuses.IN_REVIEW.TYPE:
        return t('review.status.in_review');
      case approvalStatuses.DECLINED.TYPE:
        return t('review.status.decline');
      case approvalStatuses.APPROVED.TYPE:
        return t('review.status.approved');
      // Default state is approvalStatuses.CONCEPT
      default:
        return t('review.status.concept');
    }
  }, [video]);

  const videoDimensions = useMemo(() => {
    if (!video.scenes) {
      return { width: 1920, height: 1080 };
    } else {
      const width = Math.max(...video.scenes.map(scene => scene.dimensions.width));
      const height = Math.max(...video.scenes.map(scene => scene.dimensions.height));
      return { width, height };
    }
  }, [video]);

  const handleClosePreference = useCallback(() => isPreferencesEditor && togglePreferencesEditor(false), [isPreferencesEditor]);

  const handleCloseEditThumbnail = useCallback(() => isEditThumbnail && toggleThumbnail(false), [isEditThumbnail]);

  const toggleMiniSidebar = useCallback(state => {
    if (!miniRightSidebar && (isCallToActionOpened || isChaptersEditor || isVoiceoverOpened)) {
      setCheckClosing(true);
    } else {
      if (!miniRightSidebar) {
        handleClosePreference();
        handleCloseEditThumbnail();
      }
      if (typeof state === 'boolean') setMiniRightSidebar(state);
      else setMiniRightSidebar(prevState => !prevState);
    }
  }, [handleClosePreference, handleCloseEditThumbnail, isChaptersEditor, isCallToActionOpened, isVoiceoverOpened, miniRightSidebar]);

  const onHandleEditClicked = () => {
    logFirebaseEvent('sd_edit_cl', { story_id: video.id });
    setMiniRightSidebar(false);
    handleEditClicked();
  };

  const onHandleGoToStoryboard = () => {
    setMiniRightSidebar(false);
    history.push(`/eg/storyboard/${video.id}`);
  };

  const onOpenCallToAction = useCallback(async () => {
    setMiniRightSidebar(false);
    openCallToAction();
  }, [openCallToAction]);

  const onOpenVoiceover = useCallback(async () => {
    setMiniRightSidebar(false);
    openVoiceover();
  }, [openVoiceover]);

  const handleRemoveFromReview = async () => {
    await removeFromReviewVideo();
    setIsApprovalsFeedbackMode(false);
  };

  const handleOpenChaptersEditor = useCallback(async () => {
    setMiniRightSidebar(false);
    await onChaptersEditorToggle();
    openChaptersEditor();
  }, [openChaptersEditor]);

  useEffect(() => {
    // Close all tabs when user clicks "Submit for Review"
    // This avoids displaying the VideoStatusInfo and another tab at the same time
    if (isInReviewStatus) {
      handleClosePreference();
      handleCloseEditThumbnail();
      setIsApprovalsFeedbackMode(false);
    }
  }, [isInReviewStatus]);

  useEffect(() => {
    if (miniRightSidebar) {
      handleClosePreference();
      handleCloseEditThumbnail();
      setIsApprovalsFeedbackMode(false);
    }
  }, [miniRightSidebar]);

  useEffect(() => {
    if (isApprovalFlowEnabled && isReviewerUser && (isInReviewStatus || isApprovedStatus)) {
      setIsReviewMode(true);
    }
  }, [isApprovalFlowEnabled, isReviewerUser, isInReviewStatus, isApprovedStatus]);

  useEffect(() => {
    if (checkClosing) {
      !(isCallToActionOpened || isChaptersEditor || isVoiceoverOpened) && setCheckClosing(false);
    }
  }, [isCallToActionOpened, isChaptersEditor, isVoiceoverOpened])

  const openedEditor = () => {
    if (isPreferencesEditor) {
      return <PreferenceList handleClosePreference={handleClosePreference} video={video} updateVideoSettings={updateVideoSettings} />
    }
    if (isEditThumbnail) {
      return <EditThumbnail
        staticThumbnail={
          ResourceHelper.getResourceByLabel(video, 'customStaticThumbnail')?.url ??
          ResourceHelper.getResourceByLabel(video, 'defaultStaticThumbnail')?.url
        }
        animatedThumbnail={
          ResourceHelper.getResourceByLabel(video, 'customAnimatedThumbnail')?.url ??
          ResourceHelper.getResourceByLabel(video, 'defaultAnimatedThumbnail')?.url
        }
        customStaticLoading={isThumbnailProcessing?.custom}
        customAnimatedLoading={isThumbnailProcessing.animated}
        handleCloseEditThumbnail={handleCloseEditThumbnail}
        handleSelectThumbnail={handleChooseThumbnail}
        video={video}
      />
    }
    if (isReviewMode) {
      return <ReviewVideo
        isPending={isInReviewStatus}
        isApproved={isApprovedStatus}
        storyId={video.id}
        reviewer={video.reviewer}
        handleOpenTools={() => setIsReviewMode(false)}
        approveVideo={approveVideo}
        declineVideo={declineVideo}
        revokeVideo={revokeVideo}
        miniSidebar={miniRightSidebar}
      />
    }
    if (isApprovalsFeedbackMode) {
      return <ApprovalsFeedback
        isReviewerUser={isReviewerUser}
        storyId={video.id}
        reviewer={video.reviewer}
        title={approvalsTitle}
        miniSidebar={miniRightSidebar}
        isPending={isInReviewStatus}
        handleRemoveFromReview={handleRemoveFromReview}
        handleClose={() => setIsApprovalsFeedbackMode(false)}
      />
    }
    if (isChaptersEditor) {
      return <ChaptersEditor
        duration={video.duration}
        onSave={handleSaveChapters}
        miniSidebar={miniRightSidebar}
        setMiniSidebar={setMiniRightSidebar}
        checkClosing={checkClosing}
        onCloseEditor={onChaptersEditorToggle}
      />
    }
    if (isCallToActionOpened) {
      return <CallToActionEditor
        storyId={video.id}
        videoDuration={video.duration}
        videoDimensions={videoDimensions}
        miniSidebar={miniRightSidebar}
        checkClosing={checkClosing}
        setMiniSidebar={setMiniRightSidebar}
        onCloseEditor={onCallToActionEditorToggle}
      />
    }
    if (isVoiceoverOpened) {
      return <VoiceoverEditor
        story={video}
        videoDuration={video.duration}
        miniSidebar={miniRightSidebar}
        checkClosing={checkClosing}
        loadVideoVoiceovers={loadVideoVoiceovers}
        updateVoiceoversListener={updateVoiceoversListener}
      />
    }
    return null;
  };


  return (
    <div
      className={cx(styles.wrapper, {
        [styles.mid]: isSidebarMid,
        [styles.max]: isSidebarMax,
        [styles.miniSidebar]: miniRightSidebar,
      })}
    >
      <div className={cx(styles.container, { [styles.mid]: isSidebarMid, [styles.max]: isSidebarMax })}>
        {openedEditor() ?? <>
          <div className={cx(styles.sidebarVideoHeader, { [styles.ownerHeader]: !isReviewerUser })}>
            <div className={styles.headerContainer}>
              <h2 tabIndex={0}>{t('my_video.tools.title')}</h2>
              {version !== null && !miniRightSidebar && <div className={styles.externallyEditedBadge}>
                <span tabIndex={0}>{t('my_video.tools.externally_edited.title')}</span>
              </div>}
              {isApprovalFlowEnabled && isReviewerUser && (isInReviewStatus || isApprovedStatus) && (
                <CloseButton onClose={() => setIsReviewMode(true)} />
              )}
            </div>
            <div tabIndex={-1} className={cx(styles.processingContainer, { [styles.show]: isVideoProcessing && video.videoHls === null })}>
              <Spinner width={`${miniRightSidebar ? 16 : 24}px`} height={`${miniRightSidebar ? 16 : 24}px`} size={'2px'} />
              {!miniRightSidebar && <span tabIndex={-1}>{t('my_video.is_processing')}</span>}
            </div>
          </div>
          <div className={styles.sidebarVideoOptions}>
            <SidebarButton
              visible={isApprovalFlowEnabled && !isReviewerUser && !isPreExistedStatus}
              label={t('approvals.main')}
              disabled={isExternallyEdited || isVideoProcessing}
              icon="chatReview"
              onClick={() => {
                setIsApprovalsFeedbackMode(true);
                setMiniRightSidebar(false);
              }}
              mini={miniRightSidebar}
            />

            {isApprovalFlowEnabled && !isReviewerUser && !isPreExistedStatus && <div className={styles.line} />}

            {version !== null && !miniRightSidebar && <div className={styles.externallyEditedData}>
              <div className={styles.stateSwitchContainer}>
                <SwitchList options={Object.entries(VIDEO_VERSION)} onSelect={handleChangeVersion} activeKey={version} />
              </div>
            </div>}

            <SidebarButton
              visible={isUserHasEditingRights || (isApprovalFlowEnabled && isReviewerUser)}
              label={t('my_video.tools.viewer_settings')}
              icon="preference"
              onClick={() => {
                togglePreferencesEditor(true);
                setMiniRightSidebar(false);
              }}
              mini={miniRightSidebar}
            />

            <SidebarButton
              visible={showOptionToReviewer}
              label={t('my_video.tools.storyboard')}
              icon="storyBoard"
              onClick={onHandleGoToStoryboard}
              mini={miniRightSidebar}
            />

            <SidebarButton
              visible={showOptionToReviewer}
              label={t('my_video.tools.edit')}
              icon="addClip"
              onClick={onHandleEditClicked}
              mini={miniRightSidebar}
              disabled={isExternallyEdited || isEditingDisabled}
              hoverText={isEditingDisabled ? t('my_video.warnings.old_version_tooltip') : isExternallyEdited ? t('my_video.tools.externally_edited_tooltip') : ''}
              hoverOnDisabled={true}
              badgeText={getEditButtonBadge}
            />

            <SidebarButton
              visible={showOptionToReviewer}
              disabled={isExternallyEdited || isVideoProcessing || (video.subtitlesVisible && video.subtitlesMeta.length === 0) || draft}
              label={t('my_video.tools.subtitles')}
              icon="subtitles"
              mini={miniRightSidebar}
              onClick={() => history.push(`/eg/subtitles/${video.id}`)}
              hoverText={getProcessingOrDraftTooltip}
              hoverOnDisabled
              classNameTooltip={styles.titleTooltip}
            />

            <SidebarButton
              visible={showOptionToReviewer}
              disabled={isExternallyEdited || isVideoProcessing || draft}
              label={t('my_video.tools.thumbnail')}
              icon="thumbnail"
              onClick={() => {
                toggleThumbnail(true);
                setMiniRightSidebar(false);
              }}
              mini={miniRightSidebar}
              hoverText={getProcessingOrDraftTooltip}
              hoverOnDisabled
            />

            <SidebarButton
              visible={showOptionToReviewer}
              disabled={isExternallyEdited || isVideoProcessing || draft || Math.round(video.duration / 1000) < 10}
              label={t('my_video.tools.chapters')}
              icon="chapters"
              onClick={handleOpenChaptersEditor}
              hoverText={
                getProcessingOrDraftTooltip ||
                (Math.round(video.duration / 1000) < 10 && t('my_video.tools.subtitles.video_to_short'))
              }
              hoverOnDisabled
              mini={miniRightSidebar}
              classNameTooltip={styles.titleTooltip}
            />

            <SidebarButton
              visible={showOptionToReviewer && isCallToActionEnabled}
              disabled={isExternallyEdited || isVideoProcessing || draft}
              label={t('text_box.editor.title')}
              icon="textBox"
              onClick={onOpenCallToAction}
              mini={miniRightSidebar}
              hoverText={getProcessingOrDraftTooltip}
              hoverOnDisabled
            />
            <SidebarButton
              visible={showOptionToReviewer && isVoiceoverEnabled && editorPreference?.editorType !== 'advanced'}
              disabled={isExternallyEdited || isVideoProcessing || draft}
              label={t('Voiceover')}
              icon="micro"
              onClick={onOpenVoiceover}
              mini={miniRightSidebar}
              hoverText={getProcessingOrDraftTooltip}
              hoverOnDisabled
              badgeText={"BETA"}
            />
          </div>
        </>}
      </div>
      <MiniTrigger mini={miniRightSidebar} toggle={toggleMiniSidebar} menuSite={'right'} />
    </div>
  );
};
